import { useEffect, useState } from "react";
import { supabase } from "../supabase";

export default function Support() {
  const [messages, setMessages] = useState<any[]>([]);
  const [users, setUsers] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [reply, setReply] = useState<Record<string, string>>({});
  const [sending, setSending] = useState<string | null>(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("support_messages")
      .select("*")
      .eq("is_from_user", true)
      .order("created_at", { ascending: false });

    if (data) {
      setMessages(data);
      // Загружаем профили пользователей
      const userIds = Array.from(new Set(data.map((m: any) => m.user_id)));
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, subscription_tier")
        .in("id", userIds);

      // Загружаем emails из auth
      const profileMap: Record<string, any> = {};
      if (profiles) {
        profiles.forEach((p: any) => {
          profileMap[p.id] = p;
        });
      }
      setUsers(profileMap);
    }
    setLoading(false);
  };

  const sendReply = async (userId: string) => {
    const text = reply[userId];
    if (!text?.trim()) return;
    setSending(userId);
    await supabase.from("support_messages").insert({
      user_id: userId,
      message: text.trim(),
      is_from_user: false,
    });
    setReply((prev) => ({ ...prev, [userId]: "" }));
    setSending(null);
    alert("Ответ отправлен!");
  };

  const groupedByUser = messages.reduce((acc: any, msg: any) => {
    if (!acc[msg.user_id]) acc[msg.user_id] = [];
    acc[msg.user_id].push(msg);
    return acc;
  }, {});

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString("ru-RU");
  };

  if (loading) return <div style={{ padding: 32 }}>Загрузка...</div>;

  return (
    <div>
      <div style={styles.header}>
        <h2 style={styles.title}>💬 Поддержка и отзывы</h2>
        <div style={styles.filterRow}>
          <button
            style={{
              ...styles.filterBtn,
              ...(filter === "all" ? styles.filterBtnActive : {}),
            }}
            onClick={() => setFilter("all")}
          >
            Все ({messages.length})
          </button>
          <button
            style={{
              ...styles.filterBtn,
              ...(filter === "unread" ? styles.filterBtnActive : {}),
            }}
            onClick={() => setFilter("unread")}
          >
            Новые
          </button>
          <button style={styles.refreshBtn} onClick={fetchMessages}>
            🔄 Обновить
          </button>
        </div>
      </div>

      {Object.keys(groupedByUser).length === 0 ? (
        <div style={styles.empty}>Сообщений пока нет</div>
      ) : (
        Object.entries(groupedByUser).map(([userId, msgs]: any) => (
          <div key={userId} style={styles.userBlock}>
            <div style={styles.userHeader}>
              <span style={styles.userId}>👤 {userId.substring(0, 8)}...</span>
              <span style={styles.userTier}>
                {users[userId]?.subscription_tier || "free"}
              </span>
              <span style={styles.msgCount}>{msgs.length} сообщ.</span>
            </div>
            <div style={styles.messagesList}>
              {msgs.map((msg: any) => (
                <div key={msg.id} style={styles.messageRow}>
                  <div style={styles.messageText}>{msg.message}</div>
                  <div style={styles.messageDate}>
                    {formatDate(msg.created_at)}
                  </div>
                </div>
              ))}
            </div>
            <div style={styles.replyRow}>
              <textarea
                style={styles.replyInput}
                placeholder="Ответить пользователю..."
                value={reply[userId] || ""}
                onChange={(e) =>
                  setReply((prev) => ({ ...prev, [userId]: e.target.value }))
                }
                rows={2}
              />
              <button
                style={styles.replyBtn}
                onClick={() => sendReply(userId)}
                disabled={sending === userId}
              >
                {sending === userId ? "..." : "Отправить"}
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: { marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 700, color: "#222", marginBottom: 16 },
  filterRow: { display: "flex", gap: 8, alignItems: "center" },
  filterBtn: {
    padding: "8px 16px",
    borderRadius: 8,
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    cursor: "pointer",
    fontSize: 13,
  },
  filterBtnActive: {
    backgroundColor: "#2ECC71",
    color: "#fff",
    border: "1px solid #2ECC71",
  },
  refreshBtn: {
    marginLeft: "auto",
    padding: "8px 16px",
    borderRadius: 8,
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    cursor: "pointer",
    fontSize: 13,
  },
  empty: { textAlign: "center", color: "#aaa", padding: 60, fontSize: 16 },
  userBlock: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    border: "1px solid #eee",
  },
  userHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
    paddingBottom: 12,
    borderBottom: "1px solid #f0f0f0",
  },
  userId: {
    fontSize: 13,
    fontWeight: 600,
    color: "#333",
    fontFamily: "monospace",
  },
  userTier: {
    fontSize: 11,
    padding: "2px 8px",
    borderRadius: 20,
    backgroundColor: "#d5f5e3",
    color: "#2ECC71",
    fontWeight: 600,
  },
  msgCount: { fontSize: 12, color: "#888", marginLeft: "auto" },
  messagesList: { marginBottom: 12 },
  messageRow: { padding: "10px 0", borderBottom: "1px solid #f9f9f9" },
  messageText: {
    fontSize: 14,
    color: "#222",
    marginBottom: 4,
    lineHeight: 1.5,
  },
  messageDate: { fontSize: 11, color: "#aaa" },
  replyRow: { display: "flex", gap: 8, alignItems: "flex-end" },
  replyInput: {
    flex: 1,
    padding: "10px 14px",
    borderRadius: 8,
    border: "1px solid #eee",
    fontSize: 14,
    resize: "none",
    fontFamily: "inherit",
  },
  replyBtn: {
    padding: "10px 20px",
    backgroundColor: "#2ECC71",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: "nowrap",
  },
};
